import { Navigate } from "react-router-dom";
import { getAuthItem, getAuthUser, isAuthenticated } from "../../utils/auth";
import {
  hasAnyModuleGrantForMenu,
  hasModulePermission,
  resolveModuleAccessRedirectPath,
} from "../../utils/moduleAccess";
import { hasExplicitOnmModulePermission } from "../../features/onm/onmAccess";

export default function ProtectedRoute({
  children,
  roles = [],
  teams = [],
  designations = [],
  moduleAccess = null,
  onmAccess = null,
}) {
  const user = getAuthUser();
  const fallbackRole = getAuthItem("role");
  const fallbackTeam = getAuthItem("team");
  const fallbackDepartment = getAuthItem("department");
  const fallbackDesignation =
    getAuthItem("designation_name") || getAuthItem("designation");

  const userRole = (
    user?.role || fallbackRole || ""
  ).toString().trim().toUpperCase();

  const userTeam = (
    user?.team ||
    user?.team_name ||
    user?.department ||
    fallbackTeam ||
    fallbackDepartment ||
    ""
  ).toString().trim().toUpperCase();

  const userDesignation = (
    user?.designation_name ||
    user?.designation ||
    fallbackDesignation ||
    ""
  ).toString().trim().toUpperCase();

  const allowedRoles = roles.map((role) => role.toUpperCase());
  const allowedTeams = teams.map((team) => team.toUpperCase());
  const allowedDesignations = designations.map((designation) =>
    designation.toUpperCase(),
  );

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (
    roles.length === 0 &&
    teams.length === 0 &&
    designations.length === 0 &&
    !moduleAccess &&
    !onmAccess
  ) {
    return children;
  }

  const isSuperPrivileged =
    userRole === "SUPER_ADMIN" || userRole === "ADMIN";

  const roleAllowed =
    roles.length === 0 || allowedRoles.includes(userRole);

  const teamAllowed =
    teams.length === 0 ||
    isSuperPrivileged ||
    allowedTeams.includes(userTeam);

  const designationAllowed =
    designations.length === 0 ||
    isSuperPrivileged ||
    allowedDesignations.includes(userDesignation);

  const moduleAccessAllowed = moduleAccess
    ? Array.isArray(moduleAccess.anyOf) && moduleAccess.anyOf.length > 0
      ? moduleAccess.anyOf.some((rule) =>
          rule?.subMenuKey
            ? hasModulePermission(
                user || {},
                rule.mainMenuKey,
                rule.subMenuKey,
                rule.action || "view"
              )
            : hasAnyModuleGrantForMenu(user || {}, rule?.mainMenuKey)
        )
      : (moduleAccess.subMenuKey
          ? hasModulePermission(
              user || {},
              moduleAccess.mainMenuKey,
              moduleAccess.subMenuKey,
              moduleAccess.action || "view"
            )
          : hasAnyModuleGrantForMenu(user || {}, moduleAccess.mainMenuKey))
    : false;
  const onmAccessAllowed = onmAccess
    ? hasExplicitOnmModulePermission(
        user || {},
        onmAccess.subMenuKey || "DASHBOARD",
        onmAccess.action || "view"
      )
    : false;
  const requiresOnmAccess = Boolean(onmAccess);
  const requiresModuleAccess = Boolean(moduleAccess);
  const accessGranted = requiresOnmAccess
    ? onmAccessAllowed
    : (
        requiresModuleAccess
          ? isSuperPrivileged || moduleAccessAllowed
          : roleAllowed && teamAllowed && designationAllowed
      );

  if (!accessGranted) {
    console.warn("Access Denied:", {
      userRole,
      userTeam,
      userDesignation,
      moduleAccess,
      onmAccess,
      allowedRoles,
      allowedTeams,
      allowedDesignations,
    });
    return <Navigate to={resolveModuleAccessRedirectPath(user || {}, "/active")} replace />;
  }

  return children;
}
